import { useState } from 'react';
import Navbar from '@/pages/home/components/Navbar';
import FooterSection from '@/pages/home/components/FooterSection';
import WhatsAppButton from '@/components/feature/WhatsAppButton';
import QuotePopup from '@/pages/home/components/QuotePopup';
import OrderTimeline from './components/OrderTimeline';
import ShipmentDetails from './components/ShipmentDetails';
import DocumentsPanel from './components/DocumentsPanel';
import { mockOrders, type Order } from '@/mocks/orders';

type SearchState = 'idle' | 'loading' | 'found' | 'not_found';

export default function TrackOrderPage() {
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [orderNumber, setOrderNumber] = useState('');
  const [email, setEmail] = useState('');
  const [searchState, setSearchState] = useState<SearchState>('idle');
  const [foundOrder, setFoundOrder] = useState<Order | null>(null);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearchState('loading');

    setTimeout(() => {
      const match = mockOrders.find(
        (o) =>
          o.orderNumber.toLowerCase() === orderNumber.trim().toLowerCase() &&
          o.email.toLowerCase() === email.trim().toLowerCase()
      );
      if (match) {
        setFoundOrder(match);
        setSearchState('found');
      } else {
        setFoundOrder(null);
        setSearchState('not_found');
      }
    }, 900);
  };

  const handleReset = () => {
    setSearchState('idle');
    setFoundOrder(null);
    setOrderNumber('');
    setEmail('');
  };

  return (
    <div className="min-h-screen font-sans bg-white">
      <Navbar onQuoteClick={() => setQuoteOpen(true)} />

      {/* Hero */}
      <section className="pt-40 pb-16" style={{ backgroundColor: '#0a2342' }}>
        <div className="max-w-[860px] mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 bg-white/10 text-white/80 text-xs font-semibold px-4 py-1.5 rounded-full mb-5">
            <i className="ri-ship-line" style={{ color: '#7eb8f7' }} />
            Real-Time Order Tracking
          </div>
          <h1 className="text-4xl md:text-5xl font-black text-white mb-4">Track Your Order</h1>
          <p className="text-white/60 text-base max-w-xl mx-auto">
            Enter your order number and email address to see your shipment status, vessel details, and download your documents.
          </p>
        </div>
      </section>

      <main className="max-w-[1000px] mx-auto px-6 py-12">

        {/* Search form */}
        {searchState !== 'found' && (
          <div className="max-w-[600px] mx-auto mb-12">
            <div className="bg-white rounded-2xl border border-gray-100 p-8">
              <h2 className="font-black text-lg mb-1" style={{ color: '#0a2342' }}>
                <i className="ri-search-line mr-2" />Find Your Order
              </h2>
              <p className="text-gray-500 text-sm mb-6">Your order number was sent to you in the order confirmation email.</p>

              <form onSubmit={handleSearch} className="space-y-4">
                <div>
                  <label className="block text-xs font-bold text-gray-600 mb-1.5 uppercase tracking-wide">
                    Order Number
                  </label>
                  <input
                    type="text"
                    value={orderNumber}
                    onChange={(e) => setOrderNumber(e.target.value)}
                    placeholder="e.g. SLG-2026-0312"
                    required
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-gray-400 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-600 mb-1.5 uppercase tracking-wide">
                    Email Address
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    required
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-gray-400 transition-colors"
                  />
                </div>
                <button
                  type="submit"
                  disabled={searchState === 'loading'}
                  className="w-full py-3.5 text-white font-black rounded-xl text-sm cursor-pointer active:scale-95 transition-all flex items-center justify-center gap-2"
                  style={{ backgroundColor: '#0a2342' }}
                >
                  {searchState === 'loading' ? (
                    <><i className="ri-loader-4-line animate-spin" />Searching...</>
                  ) : (
                    <><i className="ri-search-line" />Track Order</>
                  )}
                </button>
              </form>

              {/* Demo hint */}
              <div className="mt-5 pt-4 border-t border-gray-100">
                <p className="text-xs text-gray-400 font-semibold mb-2">Try a demo order:</p>
                <div className="space-y-1.5">
                  {mockOrders.map((o) => (
                    <button
                      key={o.orderNumber}
                      type="button"
                      onClick={() => { setOrderNumber(o.orderNumber); setEmail(o.email); }}
                      className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-xs cursor-pointer transition-colors text-left"
                      style={{ backgroundColor: '#f8fafc' }}
                      onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = '#e8f0fb'; }}
                      onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = '#f8fafc'; }}
                    >
                      <span className="font-bold" style={{ color: '#0a2342' }}>{o.orderNumber}</span>
                      <span className="text-gray-400">{o.flag} {o.company}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Not found state */}
            {searchState === 'not_found' && (
              <div className="mt-5 rounded-2xl border border-red-100 bg-red-50 p-6 text-center">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-red-100 mx-auto mb-3">
                  <i className="ri-search-line text-xl text-red-500" />
                </div>
                <h3 className="font-black text-gray-800 mb-1">Order Not Found</h3>
                <p className="text-sm text-gray-500 mb-4">
                  We couldn&apos;t find an order matching that number and email. Please double-check your details or contact us.
                </p>
                <a
                  href="https://wa.me/8617671262710"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white text-sm font-black px-6 py-2.5 rounded-xl cursor-pointer transition-colors"
                >
                  <i className="ri-whatsapp-line" />Contact Us on WhatsApp
                </a>
              </div>
            )}
          </div>
        )}

        {/* Order found — full details */}
        {searchState === 'found' && foundOrder && (
          <div>
            {/* Back button */}
            <button
              onClick={handleReset}
              className="flex items-center gap-2 text-sm font-semibold mb-6 cursor-pointer transition-colors"
              style={{ color: '#0a2342' }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.opacity = '0.7'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.opacity = '1'; }}
            >
              <i className="ri-arrow-left-line" />Track another order
            </button>

            <div className="space-y-5">
              {/* Timeline */}
              <OrderTimeline order={foundOrder} />

              {/* Two-column: shipment + docs */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                <ShipmentDetails order={foundOrder} />
                <DocumentsPanel order={foundOrder} />
              </div>
            </div>
          </div>
        )}

        {/* Bottom help section */}
        {searchState !== 'found' && (
          <div className="max-w-[600px] mx-auto">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[
                { icon: 'ri-mail-line', title: 'Check Your Email', desc: 'Your order number was sent in the confirmation email from support@steelink.com' },
                { icon: 'ri-whatsapp-line', title: 'WhatsApp Us', desc: 'Send your company name to +86 17671262710 and we\'ll find your order instantly' },
                { icon: 'ri-time-line', title: 'Response Time', desc: 'Our team responds within 2 hours during Mon–Fri 9AM–6PM CST' },
              ].map((h) => (
                <div key={h.title} className="text-center p-5 rounded-xl" style={{ backgroundColor: '#f8fafc' }}>
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl mx-auto mb-3" style={{ backgroundColor: '#e8f0fb' }}>
                    <i className={`${h.icon} text-base`} style={{ color: '#0a2342' }} />
                  </div>
                  <div className="text-sm font-black mb-1" style={{ color: '#0a2342' }}>{h.title}</div>
                  <div className="text-xs text-gray-500 leading-relaxed">{h.desc}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>

      <FooterSection />
      <WhatsAppButton />
      {quoteOpen && <QuotePopup onClose={() => setQuoteOpen(false)} />}
    </div>
  );
}
